import { useCallback, useEffect, useState } from "react";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Home as HomeIcon, Plus, HardHat } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const fmtMoney = (n, ccy = "INR") =>
    n == null ? "—" : `${ccy === "INR" ? "₹" : ccy + " "}${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const EMPTY = {
    name: "", kind: "Construction", status: "In Progress",
    budget: "", currency: "INR",
    start_date: "", end_date: "", vendor: "", location: "",
    notes: "",
};

const STATUS_TONE = {
    Planning: "bg-stone-50 text-stone-700 border-stone-200",
    "In Progress": "bg-sky-50 text-sky-700 border-sky-200",
    "On Hold": "bg-amber-50 text-amber-700 border-amber-200",
    Completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Cancelled: "bg-stone-50 text-stone-500 border-stone-200",
};

export default function HomeHub() {
    const { hasRole } = useAuth();
    const canEdit = hasRole && hasRole("admin", "finance", "expense_user");
    const [projects, setProjects] = useState([]);
    const [meta, setMeta] = useState({ kinds: [], statuses: [] });
    const [summary, setSummary] = useState({ project_count: 0, total_budget: 0, total_spent: 0, by_kind: [] });
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [f, setF] = useState(EMPTY);

    const load = useCallback(async () => {
        try {
            const [p, s] = await Promise.all([api.get("/home/projects"), api.get("/home/summary")]);
            setProjects(p.data || []);
            setSummary(s.data || {});
        } catch (err) { toast.error(formatApiError(err)); }
    }, []);
    useEffect(() => {
        api.get("/home/meta").then((r) => setMeta(r.data || { kinds: [], statuses: [] }));
    }, []);
    useEffect(() => { load(); }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const body = { ...f };
            if (body.budget === "" || body.budget == null) delete body.budget; else body.budget = Number(body.budget);
            ["start_date", "end_date"].forEach((k) => { if (!body[k]) delete body[k]; });
            await api.post("/home/projects", body);
            toast.success("Project added");
            setOpen(false);
            setF(EMPTY);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    return (
        <div data-testid="home-hub-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow flex items-center gap-1.5"><HomeIcon size={12} /> Home Hub</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Building, furnishing, maintaining.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-xl">
                        Group construction, interior work, appliances and AMCs into projects so you can watch
                        each budget separately without losing the individual expenses.
                    </p>
                </div>
                {canEdit && (
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="home-add">
                                <Plus size={14} className="mr-1.5" /> New project
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl" data-testid="home-add-dialog">
                            <DialogHeader>
                                <DialogTitle className="font-display text-2xl">New home project</DialogTitle>
                                <DialogDescription className="text-[13px] text-stone-500">
                                    A container for related expenses &mdash; e.g. &ldquo;Bengaluru home construction&rdquo;.
                                </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={submit} className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="col-span-2">
                                        <Label className="eyebrow">Name *</Label>
                                        <Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" placeholder="e.g. Bengaluru home construction" data-testid="hp-name" />
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Kind</Label>
                                        <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
                                            <SelectTrigger className="mt-1.5" data-testid="hp-kind"><SelectValue /></SelectTrigger>
                                            <SelectContent>{meta.kinds.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Status</Label>
                                        <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                            <SelectContent>{meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Budget (optional)</Label>
                                        <Input type="number" step="0.01" value={f.budget} onChange={(e) => setF({ ...f, budget: e.target.value })} className="mt-1.5 font-mono-figure" data-testid="hp-budget" />
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Location</Label>
                                        <Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className="mt-1.5" />
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Start date</Label>
                                        <Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className="mt-1.5" />
                                    </div>
                                    <div>
                                        <Label className="eyebrow">End date (target)</Label>
                                        <Input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} className="mt-1.5" />
                                    </div>
                                    <div className="col-span-2">
                                        <Label className="eyebrow">Vendor / builder</Label>
                                        <Input value={f.vendor} onChange={(e) => setF({ ...f, vendor: e.target.value })} className="mt-1.5" placeholder="e.g. ABC Constructions" />
                                    </div>
                                    <div className="col-span-2">
                                        <Label className="eyebrow">Notes</Label>
                                        <Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1.5" />
                                    </div>
                                </div>
                                <Button type="submit" disabled={busy} className="w-full bg-stone-900 hover:bg-stone-800 text-white" data-testid="hp-submit">
                                    {busy ? "Saving…" : "Create project"}
                                </Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {/* Portfolio tiles */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <div className="rounded-lg border border-stone-200 bg-white p-4">
                    <div className="eyebrow">Projects</div>
                    <div className="font-mono-figure text-3xl mt-1 text-stone-900">{summary.project_count}</div>
                </div>
                <div className="rounded-lg border border-stone-200 bg-white p-4">
                    <div className="eyebrow">Total budget</div>
                    <div className="font-mono-figure text-3xl mt-1 text-stone-900">{fmtMoney(summary.total_budget)}</div>
                </div>
                <div className="rounded-lg border border-stone-200 bg-white p-4">
                    <div className="eyebrow">Total spent</div>
                    <div className="font-mono-figure text-3xl mt-1 text-stone-900">{fmtMoney(summary.total_spent)}</div>
                </div>
                <div className="rounded-lg border border-stone-200 bg-white p-4">
                    <div className="eyebrow">Remaining</div>
                    <div className="font-mono-figure text-3xl mt-1 text-stone-900">{summary.remaining == null ? "—" : fmtMoney(summary.remaining)}</div>
                </div>
            </div>

            {/* Project cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {projects.length === 0 && (
                    <div className="col-span-full rounded-lg border border-dashed border-stone-300 bg-white p-14 text-center text-stone-500 text-sm" data-testid="home-empty">
                        <HardHat size={20} className="mx-auto mb-2 text-stone-400" />
                        No projects yet &mdash; add your first one to start budgeting.
                    </div>
                )}
                {projects.map((p) => {
                    const budget = p.budget || 0;
                    const pct = p.percent_used ?? 0;
                    const barColor = pct >= 100 ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500";
                    const tone = STATUS_TONE[p.status] || STATUS_TONE.Planning;
                    return (
                        <div key={p.id} className="rounded-lg border border-stone-200 bg-white p-5 hover:border-stone-300 transition-colors" data-testid={`home-card-${p.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="text-sm font-medium text-stone-900 truncate">{p.name}</div>
                                    <div className="text-[11px] text-stone-500 mt-0.5">{p.kind}{p.location ? ` · ${p.location}` : ""}</div>
                                </div>
                                <span className={`shrink-0 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${tone}`}>{p.status}</span>
                            </div>
                            <div className="mt-4 grid grid-cols-2 gap-2">
                                <div>
                                    <div className="eyebrow">Spent</div>
                                    <div className="font-mono-figure text-lg text-stone-900">{fmtMoney(p.spent || 0)}</div>
                                </div>
                                <div className="text-right">
                                    <div className="eyebrow">Budget</div>
                                    <div className="font-mono-figure text-lg text-stone-900">{budget ? fmtMoney(budget) : "—"}</div>
                                </div>
                            </div>
                            {budget > 0 && (
                                <div className="mt-3">
                                    <div className="h-1.5 rounded-full bg-stone-100 overflow-hidden">
                                        <div className={`h-full ${barColor}`} style={{ width: `${Math.min(100, pct)}%` }} />
                                    </div>
                                    <div className="flex justify-between mt-1.5 text-[11px] text-stone-500">
                                        <span>{pct?.toFixed?.(1) ?? "0"}% used</span>
                                        <span>{p.expense_count || 0} expense{p.expense_count === 1 ? "" : "s"}</span>
                                    </div>
                                </div>
                            )}
                            {(p.start_date || p.end_date) && (
                                <div className="mt-3 text-[11px] text-stone-500">
                                    {fmtDate(p.start_date) || "—"} → {fmtDate(p.end_date) || "—"}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
